import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { GitBranch, Scale, Cog } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

export const Solution = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const solutions = [
    {
      icon: GitBranch,
      title: t('solution.unifiedLedger'),
      description: t('solution.unifiedLedgerDesc'),
    },
    {
      icon: Scale,
      title: t('solution.growthIntelligence'),
      description: t('solution.growthIntelligenceDesc'),
    },
    {
      icon: Cog,
      title: t('solution.inventoryLogic'),
      description: t('solution.inventoryLogicDesc'),
    },
  ];

  return (
    <section
      ref={ref}
      className="py-24 lg:py-32 bg-primary"
      data-testid="solution-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16 lg:mb-20"
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6"
            data-testid="solution-title"
          >
            {t('solution.title')}
          </h2>
          <p
            className="text-lg text-forest-300 leading-relaxed"
            data-testid="solution-subtitle"
          >
            {t('solution.subtitle')}
          </p>
        </motion.div>

        {/* Solution Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
              className="p-8 bg-primary-light/50 rounded-xl border border-forest-600/20 hover:border-forest-500/30 hover:bg-primary-light/70 transition-all duration-300"
              data-testid={`solution-card-${index}`}
            >
              <div className="w-12 h-12 bg-forest-800/50 rounded-xl flex items-center justify-center mb-6">
                <solution.icon className="w-6 h-6 text-forest-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                {solution.title}
              </h3>
              <p className="text-forest-300/90 text-sm leading-relaxed">
                {solution.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
